import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import { CACHE_DURATIONS } from './baseApi';
import type {
  CompanySearchApiResponse,
  CompanySearchHit,
  CompanySearchParams,
  CompanySearchResult,
} from '@/lib/types/company.types';
import { normalizeSelectionIds } from '@/lib/utils/selection-ids.utils';

/**
 * Company search API.
 *
 * Both endpoints talk to the standalone Elasticsearch-backed company search
 * service through the Express proxy at `/api/company-search` — see
 * packages/server/api/company-search. The typeahead, suppression-list, group
 * and radar endpoints this slice used to carry are gone: they rode a mock base
 * query with no route behind it and had no live consumer left (location
 * typeaheads are served by `dropdownApi`, suppression lists by `listApi`,
 * saved companies by `savedCompaniesApi`).
 */

const COMPANY_SEARCH_TAG = 'CompanySearch';

/**
 * The slice of the store this API reads the signed-in user from. Declared
 * locally rather than imported as `RootState`, because store.ts imports this
 * module to register the reducer — taking its type back would be a cycle.
 */
interface AuthAwareState {
  auth?: { currentUser?: { _id?: string } | null } | null;
}

function readUserId(getState: () => unknown): string {
  return (getState() as AuthAwareState)?.auth?.currentUser?._id || '';
}

/**
 * Picks a stable per-row identifier for a company hit.
 *
 * The live company search service returns no `_id`/`id` of any kind — its
 * documents carry only display fields (name, website, description, industry,
 * comp_size_range, revenue, specialities, city/state/country, linkedin_link).
 * Reading `company._id` therefore yielded `undefined`, which is what every
 * `/company-details/${company.id}` link in the results table was built from.
 *
 * So an id is derived instead, preferring the most stable natural key
 * available: the website domain, then the LinkedIn company number, then a slug
 * of the name. A real upstream id still wins if one ever appears, so this
 * quietly stops mattering once the search service starts returning one.
 */
function deriveCompanyId(company: CompanySearchHit): string {
  const upstreamId = company?._id || company?.id || company?.companyId || company?.company_id;
  if (upstreamId) return String(upstreamId);

  const website = typeof company?.website === 'string' ? company.website.trim() : '';
  if (website) {
    return website.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
  }

  // e.g. "https://www.linkedin.com/company/239538" -> "linkedin-239538"
  const linkedinByNumber =
    typeof company?.linkedin_link_by_number === 'string' ? company.linkedin_link_by_number : '';
  const linkedinNumber = linkedinByNumber.match(/(\d{3,})/)?.[1];
  if (linkedinNumber) return `linkedin-${linkedinNumber}`;

  const name = typeof company?.name === 'string' ? company.name : '';
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || '';
}

export type { CompanySearchResult };

export interface CompanySearchResponse {
  companies: CompanySearchResult[];
  total: number;
}

/**
 * Envelope shapes the company search service has been seen to answer with.
 * Confirmed live shape is `{ total, from, size, results: [...] }`; the rest are
 * kept as fallbacks for other deployments/versions of that service.
 */
interface CompanySearchEnvelope extends Partial<CompanySearchApiResponse> {
  companies?: CompanySearchHit[];
  data?: CompanySearchHit[];
  count?: number;
  hits?: {
    total?: { value?: number };
    hits?: Array<{ _id: string; _source: Record<string, unknown> }>;
  };
}

function normalizeCompanySearchResponse(response: unknown): CompanySearchResponse {
  const body = (response ?? {}) as CompanySearchEnvelope;

  const rawCompanies: CompanySearchHit[] =
    body.results ??
    body.companies ??
    body.data ??
    body.hits?.hits?.map((hit) => ({ _id: hit._id, ...hit._source }) as CompanySearchHit) ??
    [];

  const companies: CompanySearchResult[] = rawCompanies.map((company) => ({
    ...company,
    id: deriveCompanyId(company),
    logo: company.logo || '',
    people: company.people || [],
    // uk_data companies mostly carry only uk_industry, so fall back to it.
    industry: Array.isArray(company.industry)
      ? company.industry.join(', ')
      : company.industry || company.uk_industry || '',
  }));

  const total = body.total ?? body.count ?? body.hits?.total?.value ?? companies.length;

  return { companies, total };
}

export type { CompanySearchParams };

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: axiosBaseQuery('/api/company-search'),
  tagTypes: [COMPANY_SEARCH_TAG],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: CACHE_DURATIONS.SHORT,
  endpoints: (builder) => ({
    /**
     * Company search, paginated with the service's `from`/`size`.
     *
     * userId from the authenticated session is sent as a query parameter so
     * the service can resolve which companies the user has unlocked access to.
     */
    searchCompanies: builder.query<CompanySearchResponse, CompanySearchParams>({
      queryFn: async ({ filters, page = 1, itemsPerPage = 25 }, api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '',
          method: 'POST',
          body: filters ?? {},
          params: {
            userId: readUserId(api.getState),
            from: (page - 1) * itemsPerPage,
            size: itemsPerPage,
          },
        });

        if (result.error) return { error: result.error };

        return { data: normalizeCompanySearchResponse(result.data) };
      },

      // 5 minutes - aggressively prevents duplicate calls while paging back and forth
      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      // Only refetch when the parameters actually changed
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },

      providesTags: (_result, _error, params) => [
        COMPANY_SEARCH_TAG,
        { type: COMPANY_SEARCH_TAG, id: JSON.stringify(params) },
      ],
    }),

    /**
     * Company IDs for bulk selection across pages ("Select 25/50/100/…").
     *
     * Hits the upstream's dedicated ids-only endpoint, POST
     * /api/company-search/selection, which runs the identical query with
     * `"_source": false` so Elasticsearch never serializes the document
     * bodies.
     *
     * These ids line up with the rows in the table because the live search
     * service now returns a real `id` per hit, which deriveCompanyId prefers
     * over its website/LinkedIn/slug fallbacks (verified against the live
     * service — selection ids match the `id` of the same rows from
     * /companies/search). If that ever stopped being true, the derived ids
     * would no longer match these and selection would quietly select nothing.
     */
    searchSelectionCompanyData: builder.query<string[], CompanySearchParams>({
      queryFn: async ({ filters, itemsPerPage = 25 }, api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/selection',
          method: 'POST',
          body: filters ?? {},
          params: { userId: readUserId(api.getState), from: 0, size: itemsPerPage },
        });

        if (result.error) return { error: result.error };

        return { data: normalizeSelectionIds(result.data) };
      },

      // 1 minute - selection data is temporary
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,
    }),
  }),
});

export const {
  useLazySearchCompaniesQuery,
  useLazySearchSelectionCompanyDataQuery,
} = companyApi;

export default companyApi;
